import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http'
import { APP_INITIALIZER, NgModule, inject } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MAT_LUXON_DATE_ADAPTER_OPTIONS, MatLuxonDateModule, type MatLuxonDateAdapterOptions } from '@angular/material-luxon-adapter'
import { MatIconRegistry } from '@angular/material/icon'
import { BrowserModule, DomSanitizer } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { MaterialModule } from '@app/app-material.module'
import { AppRoutingModule } from '@app/app-routing.module'
import { AppComponent } from '@app/app.component'
import { AuthInterceptor } from '@core/auth/auth-interceptor'
import { AuthService } from '@core/auth/auth.service'
import { ConfirmDialogComponent } from '@core/dialog/confirm-dialog.component'
import { EqAlDialogComponent } from '@core/dialog/eq-al-dialog.component'
import { JsonDialogComponent } from '@core/dialog/json-dialog.component'
import { SnackbarComponent } from '@core/snackbar/snackbar.component'
import { ENV_DEMO_ACCOUNT, ENV_DEMO_BYPASS_AUTH, ENV_DEMO_PASSWORD } from '@core/util/env.util'

// /**
//  * 0 = Sunday, 1 = Monday, etc
//  */
// const firstDayOfWeek = (locale: string): number => {
//   return getWeekStartByLocale(locale)
// }

const matLuxonDateAdapterOptions: MatLuxonDateAdapterOptions = { firstDayOfWeek: 1, useUtc: false, defaultOutputCalendar: 'gregory' }

// demo build : obtain a JWT before the app issues any request, so the JWT-protected gwapi calls
// are authenticated instead of returning 401 (which surfaces as the red "Server error" snackbar).
// on a real build ENV_DEMO_BYPASS_AUTH is false, so this is a no-op and the normal login flow applies.
function demoAutoLoginFactory (inAuthService: AuthService): () => Promise<void> {
  return async () => {
    if (ENV_DEMO_BYPASS_AUTH) {
      if (!inAuthService.checkJwt()) {
        await inAuthService.login({ account: ENV_DEMO_ACCOUNT, password: ENV_DEMO_PASSWORD })
      }
      // load the profile so the user-session widget has a current user (the demo guard skips this)
      if (inAuthService.checkJwt()) {
        await inAuthService.refreshCachedSelf()
      }
    }
  }
}

@NgModule({
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    MaterialModule,
    MatLuxonDateModule
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: APP_INITIALIZER, useFactory: demoAutoLoginFactory, deps: [AuthService], multi: true },
    { provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: matLuxonDateAdapterOptions }
  ],
  declarations: [
    AppComponent,

    SnackbarComponent,

    ConfirmDialogComponent,
    EqAlDialogComponent,
    JsonDialogComponent
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  MatIconRegistry = inject(MatIconRegistry)
  DomSanitizer = inject(DomSanitizer)

  // ?v bumped whenever icons.svg gains icons : the sprite is an unhashed static asset, so the
  // query string busts any stale immutable copy a browser still holds (v2 added subway-variant)
  mdiSvgSetFile = 'assets/icons/icons.svg?v=3'

  constructor () {
    this.MatIconRegistry.addSvgIconSet(this.DomSanitizer.bypassSecurityTrustResourceUrl(this.mdiSvgSetFile))
  }
}
